import wsgExcelLogoUrl from "../../assets/images&icons/wsg-excel-logo.png";

export type StyledExcelValue =
  | string
  | number
  | boolean
  | Date
  | null
  | undefined;

export interface StyledExcelColumn {
  key: string;
  header: string;
  width?: number;
}

export interface StyledExcelOptions {
  filename: string;
  rows: Record<string, StyledExcelValue>[];
  columns?: StyledExcelColumn[];
  title?: string;
  documentType?: string;
  sheetName?: string;
}

const BRAND_RED = "FFCA3827";
const BRAND_BLUE = "FF0070CD";
const BRAND_BLACK = "FF000000";
const BRAND_WHITE = "FFFFFFFF";
const BODY_TEXT = "FF474F56";

const META_ROW = 1;
const BANNER_START_ROW = 2;
const BANNER_END_ROW = 7;
const TITLE_START_ROW = 8;
const TITLE_END_ROW = 10;
const TABLE_HEADER_ROW = 12;
const TABLE_BODY_ROW_START = TABLE_HEADER_ROW + 1;
const DATA_START_COLUMN = 2;
let logoBufferPromise: Promise<ArrayBuffer | null> | null = null;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getColumnLetter(columnNumber: number): string {
  let dividend = columnNumber;
  let columnName = "";

  while (dividend > 0) {
    const modulo = (dividend - 1) % 26;
    columnName = String.fromCharCode(65 + modulo) + columnName;
    dividend = Math.floor((dividend - modulo) / 26);
  }

  return columnName;
}

function getDataColumnNumber(index: number): number {
  return DATA_START_COLUMN + index;
}

function stripExcelExtension(value: string): string {
  return value.replace(/\.xlsx$/i, "");
}

export function humanizeExcelLabel(value: string): string {
  return value
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function getExcelTitleFromFilename(filename: string): string {
  return humanizeExcelLabel(
    stripExcelExtension(filename).replace(
      /([_-]\d{2}[_-]\d{2}[_-]\d{4}|[_-]\d{4}-\d{2}-\d{2})$/i,
      ""
    )
  );
}

function sanitizeSheetName(value: string): string {
  const sanitized = value.replace(/[\\/*?:[\]]/g, " ").trim();
  return (sanitized || "Table").slice(0, 31);
}

function estimateTextLength(value: StyledExcelValue): number {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === "boolean") {
    return value ? 4 : 5;
  }
  if (value instanceof Date) {
    return 10;
  }
  return String(value).length;
}

function estimateExcelWidth(
  column: StyledExcelColumn,
  rows: Record<string, StyledExcelValue>[]
): number {
  if (typeof column.width === "number" && Number.isFinite(column.width)) {
    return clamp(column.width, 8, 45);
  }

  const maxValueLength = rows.reduce((max, row) => {
    return Math.max(max, estimateTextLength(row[column.key]));
  }, column.header.length);

  return clamp(maxValueLength + 4, 8.5, 42);
}

function buildLastUpdatedLabel(now: Date): string {
  return now.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function normalizeColumns(
  rows: Record<string, StyledExcelValue>[],
  columns?: StyledExcelColumn[]
): StyledExcelColumn[] {
  if (columns?.length) {
    return columns;
  }

  const firstRow = rows[0];
  if (!firstRow) {
    return [];
  }

  return Object.keys(firstRow).map((key) => ({
    key,
    header: key,
  }));
}

function triggerWorkbookDownload(buffer: ArrayBuffer, filename: string): void {
  const blob = new Blob(
    [buffer],
    {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }
  );
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function getLogoBuffer(): Promise<ArrayBuffer | null> {
  if (logoBufferPromise) {
    return logoBufferPromise;
  }

  logoBufferPromise = fetch(wsgExcelLogoUrl)
    .then((response) => (response.ok ? response.arrayBuffer() : null))
    .catch(() => null);

  return logoBufferPromise;
}

function applyFill(cell: any, argb: string): void {
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb },
  };
}

function applyBorder(cell: any, color: string): void {
  cell.border = {
    top: { style: "thin", color: { argb: color } },
    left: { style: "thin", color: { argb: color } },
    bottom: { style: "thin", color: { argb: color } },
    right: { style: "thin", color: { argb: color } },
  };
}

export async function exportStyledExcel({
  filename,
  rows,
  columns: columnsOption,
  title,
  documentType,
  sheetName,
}: StyledExcelOptions): Promise<void> {
  const columns = normalizeColumns(rows, columnsOption);
  if (!columns.length) {
    return;
  }

  const ExcelJSModule = await import("exceljs");
  const ExcelJS = (ExcelJSModule as any).default ?? ExcelJSModule;
  const workbook = new ExcelJS.Workbook();
  const resolvedTitle = humanizeExcelLabel(
    title?.trim() || getExcelTitleFromFilename(filename) || "WSG Export"
  );
  const resolvedDocumentType = humanizeExcelLabel(
    documentType?.trim() || resolvedTitle
  ).toLocaleUpperCase();
  const actualColumnCount = columns.length;
  const lastDataColumn = getDataColumnNumber(actualColumnCount - 1);
  const displayColumnCount = Math.max(lastDataColumn, 8);
  const lastDisplayColumn = getColumnLetter(displayColumnCount);
  const now = new Date();

  workbook.creator = "WSG";
  workbook.lastModifiedBy = "WSG";
  workbook.created = now;
  workbook.modified = now;
  workbook.calcProperties.fullCalcOnLoad = true;

  const worksheet = workbook.addWorksheet(
    sanitizeSheetName(sheetName || resolvedTitle),
    {
      properties: {
        defaultRowHeight: 20,
      },
      views: [
        {
          state: "frozen",
          ySplit: TABLE_HEADER_ROW,
          activeCell: `A${TABLE_BODY_ROW_START}`,
        },
      ],
    }
  );

  worksheet.pageSetup.margins = {
    left: 0.3,
    right: 0.3,
    top: 0.4,
    bottom: 0.4,
    header: 0.2,
    footer: 0.2,
  };

  worksheet.getRow(META_ROW).height = 22;
  worksheet.getRow(TITLE_START_ROW).height = 28;
  worksheet.getRow(TABLE_HEADER_ROW).height = 24;

  for (let columnIndex = 1; columnIndex <= displayColumnCount; columnIndex += 1) {
    const metaCell = worksheet.getCell(META_ROW, columnIndex);
    applyFill(metaCell, BRAND_RED);
    metaCell.font = {
      name: "Roboto",
      size: 12,
      bold: true,
      color: { argb: BRAND_WHITE },
    };
    metaCell.alignment = {
      vertical: "middle",
      horizontal: "left",
    };

    worksheet.getColumn(columnIndex).width =
      columnIndex >= DATA_START_COLUMN && columnIndex <= lastDataColumn
        ? estimateExcelWidth(columns[columnIndex - DATA_START_COLUMN], rows)
        : 12;

    for (let rowIndex = BANNER_START_ROW; rowIndex <= BANNER_END_ROW; rowIndex += 1) {
      applyFill(worksheet.getCell(rowIndex, columnIndex), BRAND_BLACK);
    }
  }

  for (let columnIndex = 1; columnIndex <= displayColumnCount; columnIndex += 1) {
    for (let rowIndex = BANNER_START_ROW; rowIndex <= BANNER_END_ROW; rowIndex += 1) {
      applyFill(worksheet.getCell(rowIndex, columnIndex), BRAND_WHITE);
    }
  }

  const lastUpdatedValueStartColumn = Math.max(2, displayColumnCount - 1);
  const lastUpdatedValueEndColumn = displayColumnCount;
  const lastUpdatedLabelColumn = Math.max(1, lastUpdatedValueStartColumn - 1);
  const documentTypeEndColumn = Math.max(1, lastUpdatedLabelColumn - 1);

  if (documentTypeEndColumn > 1) {
    worksheet.mergeCells(META_ROW, 1, META_ROW, documentTypeEndColumn);
  }

  const documentTypeCell = worksheet.getCell("A1");
  documentTypeCell.value = resolvedDocumentType;
  documentTypeCell.font = {
    name: "Roboto",
    size: 14,
    bold: true,
    color: { argb: BRAND_WHITE },
  };
  documentTypeCell.alignment = {
    vertical: "middle",
    horizontal: "left",
  };

  const lastUpdatedLabelCell = worksheet.getCell(META_ROW, lastUpdatedLabelColumn);
  lastUpdatedLabelCell.value = "Last Updated";
  lastUpdatedLabelCell.font = {
    name: "Roboto",
    size: 12,
    bold: true,
    color: { argb: BRAND_WHITE },
  };

  if (lastUpdatedValueEndColumn > lastUpdatedValueStartColumn) {
    worksheet.mergeCells(
      META_ROW,
      lastUpdatedValueStartColumn,
      META_ROW,
      lastUpdatedValueEndColumn
    );
  }

  const lastUpdatedValueCell = worksheet.getCell(META_ROW, lastUpdatedValueStartColumn);
  lastUpdatedValueCell.value = buildLastUpdatedLabel(now);
  lastUpdatedValueCell.font = {
    name: "Helvetica Neue",
    size: 12,
    color: { argb: BRAND_WHITE },
  };

  worksheet.mergeCells(`A${TITLE_START_ROW}:${lastDisplayColumn}${TITLE_END_ROW}`);
  const titleCell = worksheet.getCell(`A${TITLE_START_ROW}`);
  titleCell.value = resolvedTitle ? resolvedTitle.toLocaleUpperCase() : "";
  titleCell.font = {
    name: "Roboto",
    size: 22,
    bold: true,
    color: { argb: BRAND_BLUE },
  };
  titleCell.alignment = {
    vertical: "middle",
    horizontal: "left",
  };

  for (let columnIndex = 1; columnIndex <= displayColumnCount; columnIndex += 1) {
    const cell = worksheet.getCell(TITLE_START_ROW, columnIndex);
    cell.border = {
      ...cell.border,
      top: { style: "thin", color: { argb: BRAND_BLACK } },
    };
  }

  const logoBuffer = await getLogoBuffer();
  if (logoBuffer) {
    const imageId = workbook.addImage({
      buffer: logoBuffer,
      extension: "png",
    });

    worksheet.addImage(imageId, {
      tl: { col: 0.25, row: 1.25 },
      ext: { width: 223, height: 135 },
      editAs: "oneCell",
    });
  }

  columns.forEach((column, index) => {
    const cell = worksheet.getCell(TABLE_HEADER_ROW, getDataColumnNumber(index));
    cell.value = column.header;
    applyFill(cell, BRAND_BLUE);
    applyBorder(cell, BRAND_BLUE);
    cell.font = {
      name: "Roboto",
      size: 13,
      bold: true,
      color: { argb: BRAND_WHITE },
    };
    cell.alignment = {
      vertical: "middle",
      horizontal: "left",
      wrapText: true,
    };
  });

  rows.forEach((row, rowIndex) => {
    const excelRowIndex = TABLE_BODY_ROW_START + rowIndex;

    columns.forEach((column, columnIndex) => {
      const cell = worksheet.getCell(excelRowIndex, getDataColumnNumber(columnIndex));
      const value = row[column.key];

      cell.value = value ?? "";
      applyBorder(cell, BRAND_BLACK);
      cell.font = {
        name: "Helvetica Neue",
        size: 12,
        color: { argb: BODY_TEXT },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: typeof value === "number" ? "right" : "left",
        wrapText: true,
      };

      if (value instanceof Date) {
        cell.numFmt = "mm/dd/yyyy";
      }
    });
  });

  worksheet.autoFilter = {
    from: { row: TABLE_HEADER_ROW, column: DATA_START_COLUMN },
    to: { row: TABLE_HEADER_ROW, column: lastDataColumn },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  triggerWorkbookDownload(buffer as ArrayBuffer, filename);
}
